import React from 'react';
import { ArrowRight, Play, MessageCircle, Phone } from 'lucide-react';
import { heroBg } from '../data/mock';

export default function Hero() {
  return (
    <section id="top" className="relative min-h-[100svh] w-full flex items-center overflow-hidden bg-slate-950">
      {/* Background image with lighter overlays + blue glow */}
      <div className="absolute inset-0">
        <img src={heroBg} alt="Gym" className="w-full h-full object-cover" style={{ filter: 'brightness(1.05) saturate(1.05)' }} />
        {/* Left-side dim for text legibility (lighter than before) */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/75 via-slate-950/40 to-slate-950/10" />
        {/* Bottom fade so section blends into next section */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-950/85" />
        {/* Blue glow accents */}
        <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-blue-500/25 blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-sky-400/20 blur-[130px] pointer-events-none" />
        <div className="absolute top-1/3 right-1/4 w-[300px] h-[300px] rounded-full bg-blue-600/15 blur-[120px] pointer-events-none" />
      </div>

      {/* Subtle grid noise overlay (very light) */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)', backgroundSize: '3px 3px' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 w-full pt-28 md:pt-32 pb-20 md:pb-24">
        {/* Eyebrow badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-slate-950/40 backdrop-blur-md text-white/80 fade-up">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
          <span className="section-eyebrow text-[9px] sm:text-[10px] md:text-xs">UNISEX FITNESS GYM · MULTIPLE BRANCHES</span>
        </div>

        {/* Headings */}
        <h1 className="mt-5 md:mt-6 font-anton text-white text-[2.5rem] sm:text-6xl md:text-8xl lg:text-[7.5rem] leading-[0.92] fade-up" style={{ textShadow: '0 4px 30px rgba(0,0,0,0.55), 0 0 60px rgba(59,130,246,0.25)' }}>
          BODY LINE GYM
        </h1>
        <h2 className="mt-2 font-anton text-3xl sm:text-5xl md:text-7xl lg:text-8xl leading-[0.95] fade-up" style={{ textShadow: '0 4px 30px rgba(0,0,0,0.5), 0 0 40px rgba(59,130,246,0.5)' }}>
          <span className="text-blue-400">Transform Your Body.</span>
        </h2>
        <h2 className="-mt-1 font-anton text-3xl sm:text-5xl md:text-7xl lg:text-8xl leading-[0.95] hero-outline fade-up">
          Transform Your Life.
        </h2>

        <p className="mt-6 max-w-xl text-white/85 text-sm md:text-lg leading-relaxed fade-up" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
          Professional equipment. Certified trainers. Affordable memberships across multiple branches — everything you need to start strong.
        </p>

        {/* CTAs */}
        <div className="mt-7 flex flex-wrap items-center gap-2.5 fade-up">
          <a href="#plans" className="btn-join-premium group inline-flex items-center gap-2.5 px-7 md:px-9 py-4 md:py-5 text-base md:text-lg">
            <span>Join Today</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" strokeWidth={2.5} />
          </a>
          <a href="#contact" className="group inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-5 md:px-6 py-3 md:py-3.5 rounded-full shadow-xl shadow-amber-500/30 transition-all hover:-translate-y-0.5 text-sm md:text-base">
            <Play className="w-4 h-4 fill-slate-950" />
            <span className="whitespace-nowrap">Book Free Trial</span>
          </a>
          <a href="https://wa.me/918482031021" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 backdrop-blur-md border border-white/15 text-white font-medium px-4 md:px-5 py-3 md:py-3.5 rounded-full transition-colors text-sm md:text-base">
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </a>
          <a href="tel:+918482031021" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 backdrop-blur-md border border-white/15 text-white font-medium px-4 md:px-5 py-3 md:py-3.5 rounded-full transition-colors text-sm md:text-base">
            <Phone className="w-4 h-4" />
            Call Now
          </a>
        </div>

        {/* Scroll indicator - hide on mobile */}
        <div className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2">
          <span className="section-eyebrow text-white/50">SCROLL</span>
          <div className="w-px h-10 bg-gradient-to-b from-white/50 to-transparent"></div>
        </div>
      </div>
    </section>
  );
}
